import {
  Component,
  computed,
  effect,
  forwardRef,
  input,
  model,
  signal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { isNil } from '@gfe/core/utils/shared-utils';

import {
  FieldItems,
  FormBuilderBase
} from '@shared/components/row-edition-form/form-builder/form-builder.model';
import { SelectItem } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';

import { unwrapValue, wrapValue } from '../row-edition-form.utils';

@Component({
  selector: 'app-items',
  imports: [SelectComponent],
  templateUrl: './items.component.html',
  styles: [
    `
      :host {
        display: flex;
        width: 100%;
      }
    `
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ItemsComponent),
      multi: true
    }
  ]
})
export class ItemsComponent implements ControlValueAccessor {
  id = input<FormBuilderBase['id']>();
  value = model.required<string | number | null>();
  possibleValues = input.required<FieldItems['values']>();
  default = input<string | number>();
  required = input<FormBuilderBase['required']>();
  wrapperProperty = input<string | undefined>(undefined);
  useDefaultValue = signal<boolean>(false);

  constructor() {
    effect(() => {
      if (this.useDefaultValue()) {
        const value = this.possibleValues().at(0)?.value;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.value.set(value as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.onChange(value as any);
      }
    });
  }

  protected readonly selectItems = computed<SelectItem[]>(
    () =>
      this.possibleValues()?.map((item) => ({ id: item.value, label: item.label })) ?? []
  );

  onChange = (_value: string | number) => {};
  onTouched = () => {};

  protected onSelection(value: string | number | null) {
    if (isNil(value)) return;
    this.value.set(value);
    this.onChange(value);
  }

  writeValue(value: string | number): void {
    if (isNil(value)) {
      this.useDefaultValue.set(true);
    } else {
      this.value.set(unwrapValue(value, this.wrapperProperty()) as string | number);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    this.onChange = (value: string | number) =>
      fn(wrapValue(value, this.wrapperProperty()));
  }
  registerOnTouched(fn: never): void {
    this.onTouched = fn;
  }
}
